import React from 'react'
import { observable, action, makeObservable } from 'mobx'
import i18n from 'i18next'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import GettingStarted from './GettingStarted'
import EventSetup from './EventSetup'
import DataSetup from './DataSetup'
import ChartOptions from './ChartOptions'
import NonQuestionChart from './NonQuestionChart'
import MultiQuestionPicker from './MultiQuestionPicker'
import QuadrantSettings from './QuadrantSettings'
import KpiDataSetup from './KpiDataSetup'
import ChartAdvancedPreview from './ChartAdvancedPreview'
import { getQuestionTitle } from '../../../utils/QuestionHelper'
import { stripOutHtml, unescapeHtmlTags, hasRequiredDependencies } from '../../helpers'

class DashboardWizardStore {
    stages = [ // keep an eye out for errors in stages, seems to work but had to remove @observable here because new mobx wasnt working with observable array having components inside
        { label: '', component: <GettingStarted />, showInTabs: false },
        {
            label: 'Event setup', component: <EventSetup />, showInTabs: true, tabNumber: 1,
        },
        {
            label: 'Data setup', component: <DataSetup />, showInTabs: true, tabNumber: 2,
        },
        {
            label: 'Chart options', component: <ChartAdvancedPreview />, showInTabs: true, tabNumber: 3, modalClass: 'advancedPreview',
        },
    ]

    constantStages = [
        { label: '', component: <GettingStarted />, showInTabs: false },
    ]

    questionStages = [
        {
            label: 'Event setup', component: <EventSetup />, showInTabs: true, tabNumber: 1,
        },
        {
            label: 'Data setup', component: <DataSetup />, showInTabs: true, tabNumber: 2,
        },
        {
            label: 'Chart options', component: <ChartAdvancedPreview />, showInTabs: true, tabNumber: 3, modalClass: 'advancedPreview',
        },
    ]

    nonQuestionStages = [
        {
            label: 'Event setup', component: <EventSetup />, showInTabs: true, tabNumber: 1,
        },
        {
            label: 'Widget Type', component: <NonQuestionChart />, showInTabs: true, tabNumber: 2,
        },
        {
            label: 'Data setup', component: <KpiDataSetup />, showInTabs: true, tabNumber: 3,
        },
        {
            label: 'Chart options', component: <ChartAdvancedPreview />, showInTabs: true, tabNumber: 4, modalClass: 'advancedPreview',
        },
    ]

    multiQuestionStages = [
        {
            label: 'Event setup', component: <EventSetup />, showInTabs: true, tabNumber: 1,
        },
        {
            label: 'Data setup', component: <NonQuestionChart />, showInTabs: true, tabNumber: 2,
        },
        {
            label: 'Data setup', component: <KpiDataSetup />, showInTabs: true, tabNumber: 3,
        },
        {
            label: 'Question options', component: <MultiQuestionPicker />, showInTabs: true, tabNumber: 4,
        },
        {
            label: 'Chart options', component: <ChartAdvancedPreview />, showInTabs: true, tabNumber: 5, modalClass: 'advancedPreview',
        },
    ]

    quadrantStages = [
        {
            label: 'Event setup', component: <EventSetup />, showInTabs: true, tabNumber: 1,
        },
        {
            label: 'Data setup', component: <NonQuestionChart />, showInTabs: true, tabNumber: 2,
        },
        {
            label: 'Data setup', component: <KpiDataSetup />, showInTabs: true, tabNumber: 3,
        },
        {
            label: 'Chart options', component: <QuadrantSettings />, showInTabs: true, tabNumber: 4,
        },
    ]

    @observable loading = false

    @observable loadingQuestions = false

    @observable error = false

    @observable currentStage = 0

    @observable show = false

    @observable questions = []

    @observable detailedQuestions = []

    @observable newWidget = {
        widgetBasis: 'question',
        industry: '',
        respondentType: '',
        surveyType: '',
        productType: '',
        questionId: '',
        parentQuestion: '',
        displayAs: '',
        questionTypeCode: '',
        isNumeric: '',
        question: undefined,
        dataLevel: '',
        timePeriod: '',
        filter: [],
        chart: '',
        defaultSize: 2,
        multiQuestion: [{}, {}],
        nonQuestionType: '',
        quadrant: undefined,
        widgetTitle: '',
        nonQuestionKpiType: '',
        widgetRenderSettings: {},
        qlistParentId: undefined,
        customTag: undefined,
        tagOptions: [],
        optionFilter: [],
        includeRegistrationData: false,
        isMatrixKpi: false,
    }

    @observable portfolios = []

    @observable divisions = []

    @observable widgetType = undefined

    @observable showWidgetType = false

    @observable showWidgetCreator = false

    @observable multiQuestions = new Map()

    @observable multiDetailedQuestions = new Map()

    @observable fullSurvey = {}

    @observable registrationDataQuestions = []

    benchmarkTags = []

    kpis = []

    displayAsOptions = {
        average: { label: 'Average', value: 'avg' },
        averageNF: { label: 'Average', value: 'NFOption' },
        pct: { label: 'Response Percentage', value: 'pct' },
        byRow: { label: 'By Option', value: 'pct' },
        nps: { label: 'Net Promoter Score', value: 'nps' },
        total: { label: 'Total', value: 'total' },
    }

    productMap = {}

    questionDisplayAs = {
        RD: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
            nps: ['pct', 'nps'].map((d) => this.displayAsOptions[d]),
        },
        CH: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TB: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        'TB - row': {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        SB: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        OD: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        'OD - row': {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        SG: {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
            numericEvent: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
        },
        NF: {
            numeric: ['average', 'byRow'].map((d) => this.displayAsOptions[d]),
        },
        'NF - row': {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
        },
        I: {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
        },
        PC: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TX: {
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TA: {
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
    }

    blockedQuestions = ['TX', 'TA', 'TL', 'RT', 'CO', 'UV']

    blockedEventLevelQuestions = ['TL', 'RT', 'CO', 'UV']

    dataLevelOptions = {
        3: { value: 'client', label: 'Company' },
        4: { value: 'division', label: 'Division' },
        5: { value: 'portfolio', label: 'Portfolio' },
        6: { value: 'product', label: 'Event' },
    }

    dataLevels = []

    timePeriodOptions = {
        client: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        division: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        portfolio: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        product: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'last-3', label: 'Last 3 Months' },
            { value: 'last-6', label: 'Last 6 Months' },
            { value: 'last-12', label: 'Last 12 Months' },
            { value: 'last-24', label: 'Last 24 Months' },
        ],
    }

    questionChartOptions = {
        quadrantAnalysis: {
            currentLevel: ['quadrantAnalysis'],
            levelBelow: ['quadrantAnalysis'],
        },
        KPI: {
            currentLevel: ['gauge', 'gauge0-5', 'column', 'bar', 'stackedColumn', 'stackedBar', 'donut', 'pie', 'treemap', 'text'],
            levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        },
        multiQuestion: {
            currentLevel: ['column', 'bar', 'stackedColumn', 'stackedBar', 'multiText'],
            levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
        },
        LMX: {
            currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap', 'lmx'],
            levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        },
        RD: {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
            nps: {
                currentLevel: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        CH: {
            numeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        // TB: {
        //     numeric: {
        //         currentLevel: {
        //             total: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        //             row: ['guage', 'column', 'bar', 'stackedColumn', 'stackedBar', 'donut'],
        //         },
        //         levelBelow: {
        //             total: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        //             row: ['guage', 'column', 'bar', 'stackedColumn', 'stackedBar', 'donut'],
        //         },
        //     },
        //     nonNumeric: {
        //         currentLevel: {
        //             total: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        //             row: ['column', 'bar', 'stackedColumn', 'stackedBar', 'donut'],
        //         },
        //         levelBelow: {
        //             total: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        //             row: ['column', 'bar', 'stackedColumn', 'stackedBar', 'donut'],
        //         },
        //     },
        // },
        TB: {
            numeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'line'],
                    avg: ['column', 'bar', 'line', 'radar'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'line'],
                    avg: ['column', 'bar', 'line', 'radar'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        'TB - row': {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        SB: {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        // OD: {
        //     numeric: {
        //         currentLevel: {
        //             total: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        //             row: ['guage', 'column', 'bar', 'stackedColumn', 'stackedBar', 'donut'],
        //         },
        //         levelBelow: {
        //             total: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        //             row: ['guage', 'column', 'bar', 'stackedColumn', 'stackedBar', 'donut'],
        //         },
        //     },
        // },
        OD: {
            numeric: {
                currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
            },
        },
        'OD - row': {
            numeric: {
                currentLevel: ['gauge', 'gauge0-5', 'column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        SG: {
            numeric: {
                currentLevel: ['gauge', 'gauge0-5', 'column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        NF: {
            numeric: {
                currentLevel: {
                    avg: ['column', 'bar', 'line', 'text'],
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    avg: ['column', 'bar', 'line'],
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        'NF - row': {
            numeric: {
                currentLevel: {
                    avg: ['column', 'bar', 'line', 'text', 'treemap'],
                },
                levelBelow: {
                    avg: ['column', 'bar', 'line'],
                },
            },
        },
        I: {
            numeric: {
                currentLevel: ['column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        PC: {
            numeric: {
                currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
            },
        },
        TX: {
            nonNumeric: {
                currentLevel: ['textResponse', 'wordCloud'],
                levelBelow: ['textResponse', 'wordCloud'],
            },
        },
        TA: {
            nonNumeric: {
                currentLevel: ['textResponse', 'wordCloud'],
                levelBelow: ['textResponse', 'wordCloud'],
            },
        },

    }

    filterTree = []

    surveyTypes = []

    constructor(store, dashStore) {
        makeObservable(this)
        this.appStore = store
        this.dashStore = dashStore
    }

    init = async () => {
        this.loading = true
        this.productMap = await this.getProdMap()
        this.loading = false
    }

    initDataSetup = async () => {
        const { appStore, dashStore } = this
        const { oId } = dashStore
        this.loading = true
        await this.getBenchmarkTags()
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        const promises = [appStore.http.get('/portfolio'), appStore.http.get('/product')]
        if (isMultiDivision) promises.push(appStore.http.get('/division'))
        const data = await Promise.all(promises)
        this.portfolios = data[0]
        this.products = data[1]
        if (isMultiDivision) this.divisions = data[2]

        const possibleDataLevels = isMultiDivision ? this.dataLevelOptions : {
            3: { value: 'client', label: 'Company' },
            5: { value: 'portfolio', label: 'Portfolio' },
            6: { value: 'product', label: 'Event' },
        }

        this.dataLevels = Object.keys(possibleDataLevels).reduce((dl, l) => {
            if (l === 4 && !isMultiDivision) return dl
            if (l === 5 && this.portfolios.length < 2) return dl
            if (l < oId) return dl
            return [...dl, possibleDataLevels[l]]
        }, [])
        this.loading = false
    }

    setSurveyTypes = () => {
        const { oId, surveys } = this.dashStore
        if (oId === 6) {
            this.surveyTypes = surveys.reduce((types, s) => {
                if (s.surveyType && !types.includes(s.surveyType)) types.push(s.surveyType)
                return types
            }, [])
        }
    }

    createTreeData = (level, filters) => {
        const { dashStore } = this
        this.filterTree = dashStore.createTreeData(level, filters)
    }

    getMultiEventLevelQuesions = async (multiIndex, quadrantIndex) => {
        const { appStore, newWidget } = this
        const metricPath = this.widgetType === 'quadrantAnalysis'
            ? ['quadrant', quadrantIndex[multiIndex]]
            : ['multiQuestion', multiIndex]
        const respondentType = R.pathOr(newWidget.respondentType, [...metricPath, 'respondentType'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, [...metricPath, 'surveyType'], newWidget)
        const industry = R.pathOr(newWidget.industry, [...metricPath, 'industry'], newWidget)
        const { langcode } = appStore
        const { recordId, surveys } = this.dashStore

        const surveyBasic = surveys
            .filter((s) => (
                s.productId === recordId
                && s.respondentType === respondentType
                && s.surveyType === surveyType
                && s.industry === industry
            ))
            .reduce((s, cs) => (cs.year > s.year ? cs : s), { year: 0 })
        if (!surveyBasic || !surveyBasic.surveyId) {
            this.detailedQuestions = []
            return
        }
        const surveyDetailed = await this.getSurveyQuestions(surveyBasic.surveyId)

        this.fullSurvey = surveyDetailed

        const surveyQuestions = surveyDetailed.survey.pages
            .reduce((q, p) => {
                q.push(p.questions)
                return q
            }, []).flat(2).filter((q) => !!q)

        this.multiQuestions.set(multiIndex, await Promise.all(
            surveyQuestions.map((q) => this.getQuestion(q.questionId, true)),
        ))

        this.questions = this.questions
            .filter(({ question }) => !this.blockedEventLevelQuestions.includes(question.questionTypeCode))

        const { metadata } = surveyDetailed.survey
        const { productInfo } = metadata
        const { industrySpecificInfo } = productInfo
        const { productType } = industrySpecificInfo
        const group = this.getRespondentTypeName(industry, productType, surveyType, respondentType)

        this.multiDetailedQuestions.set(multiIndex, this.formatQuestions(
            this.multiQuestions.get(multiIndex),
            langcode,
            group,
            respondentType,
        ))
    }

    getMultiQuestions = async (multiIndex, quadrantIndex) => {
        const { appStore, newWidget, widgetType } = this
        const { langcode } = appStore
        const { oId, recordId } = this.dashStore
        const { clientId } = appStore.userData
        this.loadingQuestions = true

        const metricPath = widgetType === 'quadrantAnalysis'
            ? ['quadrant', quadrantIndex[multiIndex]]
            : ['multiQuestion', multiIndex]

        const industry = R.pathOr(newWidget.industry, [...metricPath, 'industry'], newWidget)
        const respondentType = R.pathOr(newWidget.respondentType, [...metricPath, 'respondentType'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, [...metricPath, 'surveyType'], newWidget)
        const productType = R.pathOr(newWidget.industry, [...metricPath, 'productType'], newWidget)

        if (oId === 6) {
            await this.getMultiEventLevelQuesions(multiIndex, quadrantIndex)
            this.loadingQuestions = false
            return
        }

        const promiseParamsArray = [
            [3, [[clientId, 3]]],
            [4, [[recordId, 4]]],
            [5, [[recordId, 5]]],
        ]

        const questions = await Promise.all(
            promiseParamsArray.map((p) => {
                if (p[0] === oId) {
                    if (widgetType === 'quadrantAnalysis') {
                        const respondentTypes = Object.values(R.pathOr(
                            {},
                            [
                                'productMap',
                                industry,
                                'children',
                                productType,
                                'children',
                                surveyType,
                                'children',
                            ],
                            this,
                        ))
                        return p[1].map((p1) => (
                            respondentTypes.map((rt) => this.getQlistQuestions(p1[0], p1[1], rt.value, surveyType, productType))
                        ))
                    }
                    return p[1].map((p1) => this.getQlistQuestions(p1[0], p1[1], respondentType, surveyType, productType))
                }
                return undefined
            }).flat(2),
        )

        this.multiQuestions.set(multiIndex, questions.flat(2).filter((q) => (
            !!q && !this.blockedQuestions.includes(q.question.questionTypeCode)
        )))

        this.multiDetailedQuestions.set(multiIndex, this.formatQuestions(this.multiQuestions.get(multiIndex), langcode))
        this.loadingQuestions = false
    }

    getQuestions = async (forQuadrant = false) => {
        const { appStore, newWidget } = this
        const { langcode } = appStore
        const { oId, recordId } = this.dashStore
        const { clientId } = appStore.userData

        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const industry = R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)
        this.loadingQuestions = true

        if (oId === 6) {
            await this.getEventLevelQuesions()
            this.loadingQuestions = false
            return
        }

        const promiseParamsArray = [
            [3, [[clientId, 3]]],
            [4, [[recordId, 4]]],
            [5, [[recordId, 5]]],
        ]

        const questions = await Promise.all(
            promiseParamsArray.map((p) => {
                if (p[0] === oId) {
                    if (forQuadrant) {
                        const respondentTypes = Object.values(R.pathOr(
                            {},
                            [
                                'productMap',
                                industry,
                                'children',
                                productType,
                                'children',
                                surveyType,
                                'children',
                            ],
                            this,
                        ))
                        return p[1].map((p1) => (
                            respondentTypes.map((rt) => this.getQlistQuestions(p1[0], p1[1], rt.value))
                        ))
                    }
                    return p[1].map((p1) => this.getQlistQuestions(p1[0], p1[1]))
                }
                return undefined
            }).flat(2),
        )

        this.questions = questions.flat(2).filter((q) => (
            !!q && !this.blockedQuestions.includes(q.question.questionTypeCode)
        ))

        this.detailedQuestions = this.formatQuestions(this.questions, langcode)
        this.loadingQuestions = false
    }

    getQuestionLabel = (question, langcode, optionContent, includeQuestion = true) => {
        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'shortTitle'], question)
        let optionLabel = ''
        if (optionContent) {
            optionLabel = R.pathOr('', ['text'], getQuestionTitle(
                {
                    content: optionContent,
                    locale: langcode,
                    defaultLocale: initialLocale,
                    initialLocale: 'en_GB',
                },
            ))
        }

        if (!includeQuestion) return optionLabel

        return `${R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale: langcode,
                defaultLocale: initialLocale,
                initialLocale: 'en_GB',
            },
        ))}${optionContent ? ` - ${optionLabel}` : ''}`
    }

    formatQuestions = (questions, langcode, group, respondentType) => (
        questions
            .filter((q) => !!q)
            .map(({ question, parentId }) => {
                const questionLabel = this.getQuestionLabel(question, langcode)
                if (!['FM', 'NF', 'OD', 'TB'].includes(question.questionTypeCode)) {
                    return ({
                        ...question,
                        group,
                        respondentType,
                        label: questionLabel,
                        value: question.id,
                        qlistParentId: parentId,
                    })
                }

                let questionPath = ['optionSet']
                if (question.questionTypeCode === 'OD') questionPath = ['optionSet', 'options']
                if (question.questionTypeCode === 'TB') questionPath = ['optionSetY', 'options']

                if (parentId) {
                    const parent = questions.find((q) => q.questionId === parentId)

                    if (R.hasPath(['question', 'optionSet'], parent)) {
                        const filteredParentOptions = R.pathOr([], ['optionSet', 'options'], parent.question)
                            .filter(({ isNonReporting }) => !isNonReporting)

                        if (question.optionSet) {
                            question.optionSet.options = [
                                ...filteredParentOptions,
                                ...question.optionSet.options,
                            ]
                        } else if (question.optionSetY) {
                            question.optionSetY.options = [
                                ...filteredParentOptions,
                                ...question.optionSetY.options,
                            ]
                        }
                    }
                }

                const formQuestion = R.pathOr(
                    [],
                    questionPath,
                    question,
                ).map((o) => {
                    let questionOption = {
                        ...o.question,
                        questionTypeCode: question.questionTypeCode === 'FM' ? o.question.questionTypeCode : o.questionTypeCode || `${question.questionTypeCode} - row`,
                        label: unescapeHtmlTags(stripOutHtml(this.getQuestionLabel(
                            question,
                            langcode,
                            ['TB', 'OD'].includes(question.questionTypeCode)
                                ? R.pathOr({}, ['langs'], o)
                                : R.pathOr({}, ['question', 'content', 'shortTitle'], o),
                        ))),
                        value: R.pathOr('', ['TB', 'OD'].includes(question.questionTypeCode) ? ['id'] : ['question', 'id'], o),
                        parentId: question.questionTypeCode !== 'FM' ? question.id : undefined,
                        formId: question.questionTypeCode === 'FM' ? question.id : undefined,
                        group,
                        respondentType,
                        qlistParentId: parentId,
                    }
                    if (['TB', 'OD'].includes(question.questionTypeCode)) {
                        questionOption = { ...question, ...questionOption }
                    }
                    return questionOption
                })
                if (question.questionTypeCode !== 'FM') {
                    formQuestion.push({
                        ...question,
                        group,
                        respondentType,
                        label: `${questionLabel} - Total`,
                        value: question.id,
                        qlistParentId: parentId,
                    })
                }
                return formQuestion
            }).flat(2).sort((a, b) => ((a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0)))
            .sort((a, b) => (((a.parentId || a.value) > (b.parentId || b.value)) ? 1
                : (((b.parentId || b.value) > (a.parentId || a.value)) ? -1 : 0)))

        // sort by label then ID to get them in alphabetical order but grouped by question
    )

    setRegistrationDataQuestions = (surveyDetailed) => {
        const pages = R.pathOr([], ['survey', 'pages'], surveyDetailed)
        const questions = pages.flatMap((p) => p.questions)
        this.registrationDataQuestions = questions.reduce((rq, q) => {
            if (q && q.hasPrepopulatedData) return [...rq, q.questionId]
            return rq
        }, [])
    }

    getEventLevelQuesions = async () => {
        const { appStore, newWidget } = this
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const industry = R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)
        const { langcode } = appStore
        const { recordId, surveys } = this.dashStore

        const surveyBasic = surveys
            .filter((s) => (
                s.productId === recordId
                && s.respondentType === respondentType
                && s.surveyType === surveyType
                && s.industry === industry
                && ((s.status !== 4
                && [1, 2, 3, 6].includes(s.productStatus)
                && s.isPrimary) || surveyType === 'EM')
            ))
            .reduce((s, cs) => (cs.year > s.year ? cs : s), { year: 0 })
        if (!surveyBasic || !surveyBasic.surveyId) {
            this.detailedQuestions = []
            return
        }
        const surveyDetailed = await this.getSurveyQuestions(surveyBasic.surveyId)

        this.fullSurvey = surveyDetailed

        this.setRegistrationDataQuestions(surveyDetailed)

        const surveyQuestions = surveyDetailed.survey.pages
            .reduce((q, p) => {
                q.push(p.questions)
                return q
            }, []).flat(2).filter((q) => !!q)

        const pageQuestionIdToQuestionId = {}
        const parentIds = {}
        surveyQuestions.forEach(({ id, questionId, parentId }) => {
            pageQuestionIdToQuestionId[id] = questionId
            if (parentId) {
                parentIds[questionId] = pageQuestionIdToQuestionId[parentId]
            }
        })

        this.questions = await Promise.all(
            surveyQuestions.map((q) => this.getQuestion(q.questionId, true)),
        )

        this.questions = this.questions
            .filter(({ question }) => !this.blockedEventLevelQuestions.includes(question.questionTypeCode))
            .map((q) => ({
                ...q,
                parentId: parentIds[q.questionId],
            }))

        const { metadata } = surveyDetailed.survey
        const { productInfo } = metadata
        const { industrySpecificInfo } = productInfo
        const { productType } = industrySpecificInfo
        const group = this.getRespondentTypeName(industry, productType, surveyType, respondentType)

        this.detailedQuestions = this.formatQuestions(
            this.questions,
            langcode,
            group,
            respondentType,
        )
    }

    getSurveyQuestions = (surveyId) => this.appStore.http.get(`/survey/${surveyId}`)

    getQlistQuestions = async (recordOverwrite, oIdOverwrite, respondentTypeOverwrite, surveyTypeOverwrite, productTypeOverwrite) => {
        const { appStore, newWidget } = this
        const oId = oIdOverwrite || this.dashStore.oId
        const recordId = recordOverwrite || this.dashStore.recordId
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)

        const includeExplori = surveyType === 'EM' ? '&includeExplori=true' : ''
        const url = `/qlist/getAllQlists2?productType=${productTypeOverwrite || productType}&surveyType=${surveyTypeOverwrite || surveyType}&respondentType=${respondentTypeOverwrite || respondentType}${includeExplori}`
        const allQuestions = await appStore.http.get(url)
        const filteredQuestions = R.uniqBy(R.prop('questionId'))(
            oId > 3
                ? allQuestions.filter((q) => (
                    (q.recordId === recordId && q.qlistoid === oId) || q.qlistoid < oId))
                : allQuestions,
        )

        const uniqueQuestionIds = []
        return filteredQuestions.reduce((qs, q) => {
            const question = JSON.parse(q.question)
            if (!uniqueQuestionIds.includes(q.questionId.toLowerCase())) {
                uniqueQuestionIds.push(q.questionId.toLowerCase())
                qs.push({ ...q, question, questionOid: R.pathOr(0, ['metadata', 'oid'], question) })
            }
            return qs
        }, [])
    }

    getQuestion = async (questionId, appendQuestionId = false) => {
        const { appStore } = this
        const url = `/api/question/${questionId.toUpperCase()}/7`
        const question = await appStore.http.get(url)
        if (appendQuestionId) {
            question.questionId = questionId
            return question
        }
        return question
    }

    resetStore = () => {
        this.currentStage = 0
        this.stages = [...this.constantStages, ...this.questionStages]
        this.widgetType = undefined
        this.newWidget = {
            widgetBasis: 'question',
            industry: '',
            respondentType: '',
            surveyType: '',
            productType: '',
            questionId: '',
            parentQuestion: '',
            displayAs: '',
            questionTypeCode: '',
            isNumeric: '',
            question: undefined,
            dataLevel: '',
            timePeriod: '',
            filter: [],
            chart: '',
            defaultSize: 2,
            multiQuestion: [{}, {}],
            nonQuestionType: '',
            quadrant: undefined,
            widgetTitle: '',
            nonQuestionKpiType: '',
            widgetRenderSettings: {},
            customTag: undefined,
            tagOptions: [],
            optionFilter: [],
            includeRegistrationData: false,
            isMatrixKpi: false,
        }
    }

    changeNonWidgetReset = (kpi) => {
        const { newWidget } = this

        const {
            widgetBasis,
        } = newWidget
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const industry = R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)

        const kpiSettings = R.pathOr({}, ['settings'], kpi)

        this.newWidget = {
            widgetBasis,
            industry,
            respondentType,
            surveyType,
            productType,
            questionId: '',
            parentQuestion: '',
            displayAs: '',
            questionTypeCode: '',
            isNumeric: '',
            question: undefined,
            dataLevel: '',
            timePeriod: '',
            filter: [],
            chart: '',
            defaultSize: 2,
            multiQuestion: [{}, {}],
            nonQuestionType: '',
            quadrant: undefined,
            widgetTitle: '',
            nonQuestionKpiType: '',
            widgetRenderSettings: {},
            kpiRenderSettings: {
                format: R.pathOr(undefined, ['format'], kpiSettings),
                dataDecimals: R.pathOr(undefined, ['decimals'], kpiSettings),
            },
            customTag: undefined,
            tagOptions: [],
            optionFilter: [],
            includeRegistrationData: false,
        }
    }

    toggleShow = (newValue) => {
        if (this.show) {
            this.resetStore()
        }
        this.show = typeof newValue === 'boolean' ? newValue : !this.show
    }

    setWidgetBasis = (basis) => {
        this.newWidget.widgetBasis = basis
        if (basis === 'question') {
            this.stages = [...this.constantStages, ...this.questionStages]
            return
        }
        this.stages = [...this.constantStages, ...this.nonQuestionStages]
    }

    addNonQuestionStages = () => {
        this.stages = [...this.constantStages, ...this.nonQuestionStages]
    }

    addMultiStages = () => {
        this.stages = [...this.constantStages, ...this.multiQuestionStages]
    }

    addQuadrantStages = () => {
        this.stages = [...this.constantStages, ...this.quadrantStages]
    }

    nextStage = () => {
        const { currentStage, stages } = this
        if (currentStage === stages.length - 1) {
            this.createWidget()
            return
        }
        this.currentStage = currentStage + 1
    }

    previousStage = () => {
        this.currentStage -= 1
    }

    goToStage = (stage) => { this.currentStage = stage }

    createWidget = () => {
        // TODO: Create the dahboard with settings from wizard
        this.toggleShow()
    }

    setError = () => {
        this.error = true
    }

    setDisplayAs = ({ name }) => {
        this.newWidget.displayAs = name
    }

    @action
    changeNewWidget = (value, field) => {
        this.newWidget[field] = value
    }

     @action
    changeNewWidgetBulk = (changes) => {
        changes.forEach((c) => { this.newWidget[c.field] = c.value })
    }

    changeMultiQuestion = (value, index) => {
        this.newWidget.multiQuestion[index] = value
    }

    addMultiQuestion = () => {
        this.newWidget.multiQuestion.push({})
    }

    removeMultiQuestion = (index) => {
        this.newWidget.multiQuestion.splice(index, 1)
    }

    getProdMap = async () => {
        const { appStore } = this
        const { clientId } = appStore.userData
        const productMaps = await appStore.http.get(`/productmap/${clientId}`)
        const prodMap = productMaps.reduce((obj, m) => {
            const newObj = { ...obj }
            // eslint-disable-next-line max-len
            newObj[m.industrycode] = newObj[m.industrycode] || { label: m.industryname, value: m.industrycode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode] = newObj[m.industrycode].children[m.producttypecode]
            || { label: m.producttypename, value: m.producttypecode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode] = newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode]
            || { label: m.surveytypename, value: m.surveytypecode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode].children[m.respondenttypecode] = newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode].children[m.respondenttypecode]
            || { label: m.respondenttypename, value: m.respondenttypecode }

            return newObj
        }, {})
        return prodMap
    }

    @action
    setChart = (chart, defaultSize) => {
        this.newWidget.chart = chart
        if (defaultSize) {
            this.newWidget.defaultSize = defaultSize
        }
    }

    setChartSize = (size) => {
        this.newWidget.defaultSize = size
    }

    addWidget = (preview = false) => {
        const widget = this.newWidget.widgetBasis === 'question'
            ? this.addQuestionWidget(preview)
            : this.addNonQuestionWidget(preview)

        if (!preview) this.toggleShow(false)
        return widget
    }

    addNonQuestionWidget = (preview) => {
        const { newWidget, dashStore, kpis } = this
        const defaultTitle = this.getDefaultTitle()
        const {
            displayAs,
            questionId,
            chart,
            question,
            defaultSize,
            timePeriod,
            dataLevel,
            parentQuestion,
            widgetTitle,
            filter,
            nonQuestionType,
            nonQuestionKpiType,
            multiQuestion,
            quadrant,
            widgetRenderSettings,
            customTag,
            tagOptions,
            optionFilter,
            includeRegistrationData,
            isMatrixKpi,
        } = newWidget

        const industry = R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)

        const multiQuestionQuadrant = quadrant && Object.keys(quadrant).length
            ? Object.keys(quadrant).map((k) => ({ ...quadrant[k], name: k }))
            : undefined
        const multiQuestionSanitized = nonQuestionType === 'multiQuestion'
            ? multiQuestion.map((q) => ({
                ...q,
                industry: R.pathOr(q.industry, ['industry', 'value'], q),
                productType: R.pathOr(q.productType, ['productType', 'value'], q),
                surveyType: R.pathOr(q.surveyType, ['surveyType', 'value'], q),
                respondentType: R.pathOr(q.respondentType, ['respondentType', 'value'], q),
            }))
            : undefined
        const widget = {
            kpiType: nonQuestionKpiType,
            questionId: '00000000-0000-0000-0000-000000000000',
            chartType: chart,
            title: widgetTitle || defaultTitle,
            width: defaultSize,
            benchmarkType: timePeriod,
            scope: dataLevel,
            industry,
            productType,
            surveyType,
            respondentType,
            parentQuestion,
            filter,
            nonQuestionType,
            multiQuestion: multiQuestionQuadrant || multiQuestionSanitized,
            questionTypeCode: R.pathOr(undefined, ['questionTypeCode'], question),
            questionOptions: R.pathOr(undefined, ['optionSetY', 'options'], question),
            questionInitialLocale: R.pathOr('en_GB', ['metadata', 'initialLocale'], question),
            widgetRenderSettings,
            key: uuidv4(),
            customTag,
            tagOptions,
            optionFilter,
            includeRegistrationData,
            isMatrixKpi,
        }
        if (!preview) dashStore.addWidget(widget)
        return widget
    }

    addQuestionWidget = (preview) => {
        const { newWidget, dashStore } = this
        const defaultTitle = this.getDefaultTitle()
        const {
            displayAs,
            questionId,
            chart,
            question,
            defaultSize,
            timePeriod,
            dataLevel,
            parentQuestion,
            widgetTitle,
            filter,
            widgetRenderSettings,
            qlistParentId,
            customTag,
            tagOptions,
            optionFilter,
            includeRegistrationData,
        } = newWidget

        const industry = R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)

        const widget = {
            kpiType: displayAs,
            questionId,
            chartType: chart,
            title: widgetTitle || defaultTitle,
            width: defaultSize,
            benchmarkType: timePeriod,
            scope: dataLevel,
            industry,
            productType,
            surveyType,
            respondentType,
            parentQuestion,
            filter,
            questionTypeCode: question.questionTypeCode,
            formId: question.formId,
            questionOptions: R.pathOr(undefined, ['optionSetY', 'options'], question),
            questionInitialLocale: R.pathOr('en_GB', ['metadata', 'initialLocale'], question),
            widgetRenderSettings,
            key: uuidv4(),
            qlistParentId,
            customTag,
            tagOptions,
            optionFilter,
            includeRegistrationData,
        }
        if (!preview) dashStore.addWidget(widget)
        return widget
    }

    getNonQuestionData = async () => {
        this.loading = true
        await this.getKpis()
        this.loading = false
    }

    getKpis = async () => {
        try {
            const kpis = await this.appStore.http.get('/kpi')
            this.kpis = kpis
        } catch (e) {
            this.kpis = []
        }
    }

    getDefaultTitle = () => {
        const { newWidget, kpis, appStore } = this
        const { langcode } = appStore
        if (newWidget.widgetTitle) return newWidget.widgetTitle
        if (newWidget.nonQuestionType === 'quadrantAnalysis') return 'Quadrant Analysis'
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)

        let defaultTitle
        if (newWidget.widgetBasis !== 'question') {
            defaultTitle = newWidget.nonQuestionType === 'KPI'
                ? kpis.find((k) => (
                    k.kpiId === newWidget.nonQuestionKpiType
                        && k.respondentType === respondentType
                )).name
                : newWidget.nonQuestionType
        } else {
            defaultTitle = R.pathOr('', ['question', 'content', 'shortTitle', langcode, 'text'], newWidget)
        }

        return defaultTitle
    }

    getAllowedKpis = () => {
        const allowedKpiQuestions = this.questions.reduce((set, q) => (
            set.add(q.questionId.toLowerCase())
        ), new Set())
        return this.kpis
            .filter((k) => (
                R.pathOr(
                    false,
                    [
                        'productMap',
                        k.industry,
                        'children',
                        k.productType,
                        'children',
                        k.surveyType,
                        'children',
                        k.respondentType,
                    ],
                    this,
                )
            ))
            .reduce(({ kpis, allowedKpis }, kpi) => {
                if (hasRequiredDependencies(kpi, allowedKpiQuestions, allowedKpis)) {
                    kpis.push(kpi)
                    allowedKpis.add(kpi.kpiId.toLowerCase())
                }
                return {
                    kpis,
                    allowedKpis,
                }
            }, {
                kpis: [],
                allowedKpis: new Set(),
            }).kpis
    }

    getRespondentTypeName = (industry, productType, surveyType, respondentType) => R.pathOr(
        'KPIs',
        [
            'productMap',
            industry,
            'children',
            productType,
            'children',
            surveyType,
            'children',
            respondentType,
            'label',
        ],
        this,
    )

    getQuadrantQuestions = (withLMAT = true, forMultiQuestion = false) => {
        const { newWidget } = this
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const quadrantAllowedQuestions = this.questions
            .filter((q) => !!q.allowQuadrantAnalysis || forMultiQuestion)
            .reduce((obj, q) => (
                {
                    ...obj,
                    [q.questionId.toLowerCase()]:
                    {
                        respondenttypename: q.respondenttypename,
                        respondentTypeCode: q.respondentTypecode,
                    },
                }
            ), {})

        const quadrantKpis = this.getAllowedKpis()
            .filter((kpi) => kpi.settings.allowQuadrantAnalysis && kpi.productType === productType)

        const groupedQuestions = R.groupBy((q) => q.group,
            [
                ...quadrantKpis.map((k) => ({
                    label: k.name,
                    value: k.kpiId,
                    group: this.getRespondentTypeName(k.industry, k.productType, k.surveyType, k.respondentType),
                    respondentType: k.respondentType,
                })),
                ...this.detailedQuestions
                    .filter((q) => Object.keys(quadrantAllowedQuestions).includes(q.id.toLowerCase()) || forMultiQuestion)
                    .map((q) => {
                        const questionId = q.formId || q.parentId || q.id
                        return {
                            ...q,
                            group: R.or(q.group, quadrantAllowedQuestions[questionId.toLowerCase()].respondenttypename),
                            respondentType: R.or(q.respondentType, quadrantAllowedQuestions[questionId.toLowerCase()].respondentTypeCode),
                        }
                    }).filter((q) => (forMultiQuestion ? !['TB', 'NF', 'FM'].includes(q.questionTypeCode) : true)),
            ])

        return Object.keys(groupedQuestions).map((g) => ({ label: g, options: groupedQuestions[g] }))
    }

    getMultiQuadrantQuestions = (withLMAT = true, forMultiQuestion = false, multiIndex, quadrantIndex) => {
        const { newWidget } = this
        const productType = R.pathOr(newWidget.productType, ['quadrant', quadrantIndex[multiIndex], 'productType'], newWidget)

        const quadrantAllowedQuestions = (this.multiQuestions.get(multiIndex) || [])
            .filter((q) => !!q.allowQuadrantAnalysis || forMultiQuestion)
            .reduce((obj, q) => (
                {
                    ...obj,
                    [q.questionId.toLowerCase()]:
                    {
                        respondenttypename: q.respondenttypename,
                        respondentTypeCode: q.respondentTypecode,
                    },
                }
            ), {})

        const quadrantKpis = this.getAllowedKpis()
            .filter((kpi) => kpi.settings.allowQuadrantAnalysis && kpi.productType === productType)

        const groupedQuestions = R.groupBy((q) => q.group,
            [
                ...quadrantKpis.map((k) => ({
                    label: k.name,
                    value: k.kpiId,
                    group: this.getRespondentTypeName(k.industry, k.productType, k.surveyType, k.respondentType),
                    respondentType: k.respondentType,
                })),
                ...(this.multiDetailedQuestions.get(multiIndex) || [])
                    .filter((q) => Object.keys(quadrantAllowedQuestions).includes(q.id.toLowerCase()) || forMultiQuestion)
                    .map((q) => {
                        const questionId = q.formId || q.parentId || q.id
                        return {
                            ...q,
                            group: R.or(q.group, quadrantAllowedQuestions[questionId.toLowerCase()].respondenttypename),
                            respondentType: R.or(q.respondentType, quadrantAllowedQuestions[questionId.toLowerCase()].respondentTypeCode),
                        }
                    }).filter((q) => (forMultiQuestion ? !['TB', 'NF', 'FM'].includes(q.questionTypeCode) : true)),
            ])

        return Object.keys(groupedQuestions).map((g) => ({ label: g, options: groupedQuestions[g] }))
    }

    getBenchmarkTags = async () => {
        if (process.env.USE_CUSTOM_BENCHMARKS !== 'true') {
            this.benchmarkTags = []
            return
        }
        const benchmarkTags = await this.appStore.http.get('/benchmarkTags')
        this.benchmarkTags = benchmarkTags.benchmarkTags
    }

    toggleShowWidgetType = (openWidgetCreator) => {
        const { dashStore } = this
        const { toggleShowWidgetCreator } = dashStore
        if (!this.showWidgetType) {
            this.showWidgetType = true
            return
        }
        if (!openWidgetCreator) {
            this.showWidgetType = false
            this.widgetType = undefined
            return
        }
        this.showWidgetType = false
        toggleShowWidgetCreator()
    }

    changeWidgetType = (value) => {
        this.widgetType = value
    }

    toggleShowWidgetCreator = () => {
        if (this.showWidgetCreator) this.resetStore()
        this.showWidgetCreator = !this.showWidgetCreator
    }
}

export default DashboardWizardStore
